import React, { useMemo, useRef, useState } from 'react';
import Icon from '../AppIcon';
import { FieldShell } from './FormField';

// Selezione con ricerca mentre si scrive (al posto di un <select> con centinaia di voci).
//
// Ricerca: il testo si divide in termini separati da "%" o da spazi; una voce compare se contiene TUTTI i termini,
// in qualsiasi ordine, senza distinguere maiuscole/minuscole né accenti.
//   "mot%ruot%cca"  trova  "MOTORE RUOTA CCA"  e  "CCA · MOTRICE · MOTORE RUOTA"
// Tutto avviene sulle voci già caricate (opzioni), quindi è istantaneo.

const MAX_VISIBILI = 50;

const normalizza = (t) => String(t ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Termini di ricerca da un testo digitato (separatori: % e spazi). */
export const terminiRicerca = (testo) => normalizza(testo).split(/[%\s]+/).filter(Boolean);

/** opzioni: [{ value, label, disabled? }] */
const SelectRicerca = ({
  label, name, value, onChange, opzioni = [], errore, obbligatorio, disabilitato,
  segnaposto = 'Cerca... (es. mot%ruot%cca)', suggerimento, caricamento = false,
}) => {
  const [testo, setTesto] = useState('');
  const [attivo, setAttivo] = useState(0); // indice evidenziato (tastiera)
  const inputRef = useRef(null);

  const selezionata = useMemo(() => opzioni.find((o) => o.value === value) ?? null, [opzioni, value]);

  const indicizzate = useMemo(
    () => opzioni.map((o) => ({ ...o, _n: normalizza(o.label) })),
    [opzioni],
  );

  const risultati = useMemo(() => {
    const termini = terminiRicerca(testo);
    if (termini.length === 0) return indicizzate;
    return indicizzate.filter((o) => termini.every((t) => o._n.includes(t)));
  }, [indicizzate, testo]);

  const visibili = risultati.slice(0, MAX_VISIBILI);

  const scegli = (o) => {
    if (!o || o.disabled) return;
    setTesto('');
    setAttivo(0);
    onChange(o.value);
  };

  const cambia = () => {
    onChange('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const tasto = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAttivo((i) => Math.min(i + 1, visibili.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAttivo((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // Invio sceglie la voce evidenziata e non invia il form
      e.preventDefault();
      scegli(visibili[attivo]);
    }
  };

  // voce già scelta
  if (selezionata) {
    return (
      <FieldShell label={label} name={name} obbligatorio={obbligatorio} errore={errore} suggerimento={suggerimento}>
        <div className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 border ${
          errore ? 'bg-red-500/5 border-red-500/40' : 'bg-primary/5 border-primary/20'
        } ${disabilitato ? 'opacity-60' : ''}`}
        >
          <span id={name} className="text-sm font-bold text-foreground break-words min-w-0">{selezionata.label}</span>
          {!disabilitato && (
            <button
              type="button"
              onClick={cambia}
              className="shrink-0 px-3 py-2 rounded-xl bg-muted/60 hover:bg-muted border border-border/40 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:text-foreground transition-all"
            >
              Cambia
            </button>
          )}
        </div>
      </FieldShell>
    );
  }

  return (
    <FieldShell label={label} name={name} obbligatorio={obbligatorio} errore={errore} suggerimento={suggerimento}>
      <div className="flex flex-col gap-2">
        <div className="relative">
          <Icon name={caricamento ? 'Loader2' : 'Search'} size={14} className={`absolute left-3.5 top-1/2 -translate-y-1/2 text-primary/60 ${caricamento ? 'animate-spin' : ''}`} />
          <input
            ref={inputRef}
            id={name}
            name={name}
            type="text"
            value={testo}
            onChange={(e) => { setTesto(e.target.value); setAttivo(0); }}
            onKeyDown={tasto}
            disabled={disabilitato || caricamento}
            placeholder={segnaposto}
            autoComplete="off"
            aria-invalid={!!errore}
            className={`w-full rounded-2xl pl-10 pr-4 py-3 text-sm font-bold outline-none transition-all ${
              errore
                ? 'bg-red-500/5 border-2 border-red-500/40 focus:border-red-500/60 focus:ring-2 focus:ring-red-500/10'
                : 'bg-muted/30 border border-border/30 focus:border-primary/50 focus:bg-background focus:ring-2 focus:ring-primary/10 hover:border-border/60'
            } ${disabilitato ? 'opacity-50 cursor-not-allowed' : ''}`}
          />
        </div>

        {!caricamento && (
          risultati.length === 0 ? (
            <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/40 px-1">Nessun risultato.</p>
          ) : (
            <div className="border border-border/40 rounded-2xl overflow-hidden bg-card">
              <ul role="listbox" className="max-h-[15.5rem] overflow-y-auto overscroll-contain divide-y divide-border/50">
                {visibili.map((o, i) => (
                  <li key={o.value} role="option" aria-selected={i === attivo}>
                    <button
                      type="button"
                      disabled={o.disabled}
                      onClick={() => scegli(o)}
                      onMouseEnter={() => setAttivo(i)}
                      className={`w-full text-left px-4 py-2.5 text-[13px] font-bold border-l-4 cursor-pointer transition-colors disabled:opacity-40 ${
                        i === attivo
                          ? 'bg-primary/15 text-foreground border-l-primary'
                          : `border-l-transparent text-foreground/80 hover:bg-primary/10 hover:border-l-primary/50 ${i % 2 ? 'bg-muted/25' : ''}`
                      }`}
                    >
                      {o.label}
                    </button>
                  </li>
                ))}
              </ul>
              {risultati.length > visibili.length && (
                <p className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-muted-foreground/50 bg-muted/20 border-t border-border/20">
                  Primi {visibili.length} di {risultati.length}: scrivi di più per restringere.
                </p>
              )}
            </div>
          )
        )}
      </div>
    </FieldShell>
  );
};

export default SelectRicerca;
